// src/pages/templates/components/EnhancedTemplateEditor.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Save, 
  Eye, 
  Code, 
  Layout, 
  FileText, 
  Download, 
  Copy, 
  Plus, 
  X, 
  Wand2, 
  Loader2, 
  CheckCircle, 
  AlertTriangle,
  Maximize2,
  Grid,
  Settings
} from 'lucide-react';

// Import the template rendering hook
import { useTemplateRendering } from '../../../hooks/useTemplateRendering';

const EnhancedTemplateEditor = ({
  templateId = null,
  initialTemplate = null,
  onSave,
  onCancel,
  onChange,
  isSaving = false,
  isEditMode = false,
  isModal = false,
  hideHeader = false
}) => {
  // State management
  const [templateName, setTemplateName] = useState(initialTemplate?.name || 'Template Nou');
  const [templateContent, setTemplateContent] = useState(initialTemplate?.content || '');
  const [viewMode, setViewMode] = useState('split'); // 'split', 'editor', 'preview', 'fullscreen'
  const [renderMode, setRenderMode] = useState('rendered');
  const [showVariables, setShowVariables] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const editorRef = useRef(null);

  // Use the template rendering hook
  const {
    renderedContent,
    isRendering,
    error: renderError,
    variables,
    missingVariables,
    isValid,
    sampleData,
    variableCategories,
    updateSampleData,
    resetSampleData,
    exportToPdf,
    validateTemplate,
    hasUnsavedChanges: hasDataChanges
  } = useTemplateRendering(templateContent);

  // Track overall unsaved changes
  useEffect(() => {
    const contentChanged = templateContent !== (initialTemplate?.content || '');
    const nameChanged = templateName !== (initialTemplate?.name || 'Template Nou');
    setHasUnsavedChanges(contentChanged || nameChanged || hasDataChanges);
    
    // Notify parent component of changes
    if (onChange) {
      onChange(templateName, templateContent);
    }
  }, [templateContent, templateName, hasDataChanges, initialTemplate, onChange]);

  // Handle save
  const handleSave = async () => {
    if (!isValid) {
      alert('Template-ul conține erori și nu poate fi salvat.');
      return;
    }

    try {
      const templateData = {
        name: templateName,
        content: templateContent,
        isActive: true
      };

      await onSave(templateData);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Eroare la salvarea template-ului: ' + error.message);
    }
  };

  // Handle export to PDF
  const handleExportPdf = async () => {
    if (templateId) {
      const result = await exportToPdf(templateId, `${templateName.replace(/\s+/g, '_')}.pdf`);
      if (!result.success) {
        alert('Eroare la exportul PDF: ' + result.error);
      }
    } else {
      // For new templates, we can't export via API, so we'll show the rendered content
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>${templateName}</title>
            <style>
              body { font-family: Georgia, serif; line-height: 1.6; margin: 40px; }
              .contract-header { text-align: center; margin-bottom: 2rem; border-bottom: 2px solid #333; padding-bottom: 1rem; }
              .contract-header h1 { font-size: 1.5rem; font-weight: bold; margin-bottom: 1rem; text-transform: uppercase; }
            </style>
          </head>
          <body>
            ${renderedContent}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Insert variable at cursor position
  const insertVariable = (variableKey) => {
    const editor = editorRef.current;
    if (!editor) return;

    const start = editor.selectionStart;
    const end = editor.selectionEnd;
    const text = editor.value;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);
    const variableTag = `{{${variableKey}}}`;
    
    const newContent = before + variableTag + after;
    setTemplateContent(newContent);
    
    // Set cursor position after the inserted variable
    setTimeout(() => {
      editor.focus();
      editor.setSelectionRange(start + variableTag.length, start + variableTag.length);
    }, 0);
  };

  // Get container height based on context
  const getContainerHeight = () => {
    if (isModal) {
      return 'h-[calc(100vh-160px)]'; // Account for modal header/padding
    }
    return 'h-[calc(100vh-200px)]'; // Regular page layout
  };

  return (
    <div className={`flex flex-col ${getContainerHeight()}`}>
      {/* Header - only show if not hidden */}
      {!hideHeader && (
        <div className={`bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0 ${isModal ? 'border-t-0' : ''}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {!isModal && (
                <div className="flex items-center space-x-2">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="text-lg font-semibold bg-transparent border-none outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                    placeholder="Numele template-ului..."
                  />
                </div>
              )}
              
              {isModal && (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="text-lg font-semibold bg-transparent border border-gray-300 outline-none focus:ring-2 focus:ring-blue-500 rounded px-3 py-2"
                    placeholder="Numele template-ului..."
                  />
                </div>
              )}
              
              {/* Validation Status */}
              <div className="flex items-center space-x-2">
                {isValid ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    <span className="text-sm">Valid</span>
                  </div>
                ) : (
                  <div className="flex items-center text-red-600">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    <span className="text-sm">Erori: {missingVariables.length}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* View Mode Selector */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('split')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'split' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Grid className="w-4 h-4 mr-1 inline" />
                  Split
                </button>
                <button
                  onClick={() => setViewMode('editor')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'editor' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Code className="w-4 h-4 mr-1 inline" />
                  Editor
                </button>
                <button
                  onClick={() => setViewMode('preview')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'preview' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Eye className="w-4 h-4 mr-1 inline" />
                  Preview
                </button>
                <button
                  onClick={() => setViewMode('fullscreen')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'fullscreen' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Maximize2 className="w-4 h-4 mr-1 inline" />
                  Full
                </button>
              </div>

              {/* Action Buttons */}
              <button
                onClick={handleExportPdf}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm"
                disabled={!isValid}
              >
                <Download className="w-4 h-4 mr-1 inline" />
                Export PDF
              </button>

              <button
                onClick={handleSave}
                disabled={!isValid || isSaving || !hasUnsavedChanges}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 inline animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1 inline" />
                    Salvează
                  </>
                )}
              </button>

              {!isModal && onCancel && (
                <button
                  onClick={onCancel}
                  className="px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                >
                  Anulează
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-1 min-h-0">
        {/* Variables Sidebar */}
        {showVariables && (
          <div className="w-80 bg-gray-50 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Variabile ({variables.length})</h3>
                <button
                  onClick={() => setShowVariables(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Click pe o variabilă pentru a o insera
              </p>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {variableCategories.map(category => {
                const categoryVariables = variables.filter(v => v.category === category);
                return (
                  <div key={category} className="border-b border-gray-200">
                    <div className="p-3 bg-gray-100">
                      <h4 className="font-medium text-gray-900 text-sm">{category}</h4>
                    </div>
                    <div className="p-3 space-y-2">
                      {categoryVariables.map(variable => (
                        <div key={variable.key} className="space-y-2">
                          <button
                            onClick={() => insertVariable(variable.key)}
                            className="w-full text-left p-2 text-sm bg-white border border-gray-200 rounded hover:bg-blue-50 hover:border-blue-300 transition-colors"
                          >
                            <div className="font-medium text-gray-900">
                              {variable.label}
                            </div>
                            <div className="text-xs text-gray-500 font-mono">
                              {`{{${variable.key}}}`}
                            </div>
                          </button>
                          
                          <div className="px-2">
                            {variable.type === 'textarea' ? (
                              <textarea
                                value={sampleData[variable.key] || ''}
                                onChange={(e) => updateSampleData(variable.key, e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded resize-none"
                                rows={3}
                                placeholder={`Valoare pentru ${variable.label}`}
                              />
                            ) : (
                              <input
                                type={variable.type}
                                value={sampleData[variable.key] || ''}
                                onChange={(e) => updateSampleData(variable.key, e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                placeholder={`Valoare pentru ${variable.label}`}
                              />
                            )}
                            
                            <div className="text-xs text-gray-500 font-mono bg-gray-50 px-2 py-1 rounded mt-1">
                              {`{{${variable.key}}}`}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Editor & Preview */}
        <div className="flex-1">
          <div className="bg-white h-full border border-gray-200">
            {viewMode === 'split' ? (
              <div className="flex h-full">
                {/* Editor Half */}
                <div className="w-1/2 border-r border-gray-200 flex flex-col">
                  <div className="p-3 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">Editor HTML</h4>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">
                          {templateContent.length} caractere
                        </span>
                        <span className="text-xs text-gray-500">
                          {variables.length} variabile
                        </span>
                      </div>
                    </div>
                  </div>
                  <textarea
                    ref={editorRef}
                    value={templateContent}
                    onChange={(e) => setTemplateContent(e.target.value)}
                    className="flex-1 p-4 font-mono text-sm border-none resize-none focus:outline-none"
                    placeholder="Introduceți conținutul template-ului..."
                  />
                </div>

                {/* Preview Half */}
                <div className="w-1/2 flex flex-col">
                  <div className="p-3 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">
                        Preview {renderMode === 'rendered' ? '(Cu Date)' : '(Variabile)'}
                        {isRendering && <Loader2 className="w-4 h-4 inline ml-2 animate-spin" />}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            const content = renderMode === 'rendered' ? renderedContent : templateContent;
                            navigator.clipboard.writeText(content);
                          }}
                          className="p-1 text-gray-500 hover:text-gray-700"
                          title="Copiază conținutul"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setRenderMode(renderMode === 'rendered' ? 'raw' : 'rendered')}
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors"
                        >
                          <Wand2 className="w-4 h-4 mr-1 inline" />
                          {renderMode === 'rendered' ? 'Arată Variabile' : 'Arată cu Date'}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div 
                    className="flex-1 p-4 overflow-y-auto bg-white"
                    style={{
                      fontFamily: 'Georgia, serif',
                      lineHeight: '1.6'
                    }}
                  >
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: renderMode === 'rendered' ? renderedContent : templateContent 
                      }}
                      style={{ maxWidth: 'none' }}
                    />
                  </div>
                </div>
              </div>
            ) : viewMode === 'editor' ? (
              <div className="h-full flex flex-col">
                <div className="p-3 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">Editor HTML</h4>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">
                        {templateContent.length} caractere
                      </span>
                      <span className="text-xs text-gray-500">
                        {variables.length} variabile
                      </span>
                    </div>
                  </div>
                </div>
                <textarea
                  ref={editorRef}
                  value={templateContent}
                  onChange={(e) => setTemplateContent(e.target.value)}
                  className="flex-1 p-4 font-mono text-sm border-none resize-none focus:outline-none"
                  placeholder="Introduceți conținutul template-ului..."
                />
              </div>
            ) : (
              <div className="h-full flex flex-col">
                <div className="p-3 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">
                      Preview Complet {renderMode === 'rendered' ? '(Cu Date)' : '(Variabile)'}
                      {isRendering && <Loader2 className="w-4 h-4 inline ml-2 animate-spin" />}
                    </h4>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setRenderMode(renderMode === 'rendered' ? 'raw' : 'rendered')}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors"
                      >
                        <Wand2 className="w-4 h-4 mr-1 inline" />
                        {renderMode === 'rendered' ? 'Arată Variabile' : 'Arată cu Date'}
                      </button>
                    </div>
                  </div>
                </div>
                <div 
                  className="flex-1 p-6 overflow-y-auto bg-white"
                  style={{
                    fontFamily: 'Georgia, serif',
                    lineHeight: '1.8',
                    fontSize: '16px'
                  }}
                >
                  <div 
                    dangerouslySetInnerHTML={{ 
                      __html: renderMode === 'rendered' ? renderedContent : templateContent 
                    }}
                    style={{ maxWidth: 'none' }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Show Variables Toggle (when sidebar is hidden) */}
        {!showVariables && (
          <button
            onClick={() => setShowVariables(true)}
            className="fixed right-4 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white p-3 rounded-l-lg shadow-lg hover:bg-blue-700 transition-colors z-10"
            title="Arată variabilele"
          >
            <Settings className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default EnhancedTemplateEditor;